import { ColumnDef } from "@tanstack/react-table";
import _ from "lodash";
import React, { useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import RoleRequire from "../../../../helpers/auth/RoleRequire";
import { t } from "i18next";
import { TbActivityHeartbeat, TbCircleCheckFilled, TbCircleFilled, TbDisabled } from "react-icons/tb";
import { TriggersTableData } from "../../../../types/formDataTypes";
import { Button } from "../../../../ui/Button";
import { SimpleDataTable } from "../../../../ui/DataTable/SimpleDataTable";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogOverlay,
  DialogTitle,
} from "../../../../ui/Dialog";
import { Input } from "../../../../ui/Input/Input";
import AddTrigger from "./AddTrigger";
import TriggerItem from "./triggerItem/TriggerItem";
import { TriggersContext } from "./Triggers";

export default function TriggersGrid() {
  const { triggers } = useContext(TriggersContext)!;
  const { t } = useTranslation();
  const [selectedTrigger, setSelectedTrigger] = useState();
  const [search, setSearch] = React.useState("");
  const [openCreateModal, setOpenCreateModal] = useState<boolean>(false);
  const [openEditModal, setOpenEditModal] = useState<boolean>(false);

  let nodes = triggers?.map((data) => {
    return {
      id: data.id,
      name: data.attributes.name,
      type: data.attributes.type,
      enabled: data.attributes.enabled,
      sensor_id: data.attributes.sensor_id,
      sensor_type_id: data.attributes.sensor_type_id,
      last_triggered_at: data.attributes.last_triggered_at ? data.attributes.last_triggered_at : null,
      status_change_to: data.attributes.status_change_to,
      string_value_equal: data.attributes.string_value_equal,
      value_less_than: data.attributes.value_less_than,
      value_more_than: data.attributes.value_more_than,
      min_triggered_period: data.attributes.min_triggered_period,
    };
  });

  // Сортировка по умолчанию
  nodes = _.sortBy(nodes, []);
  let data = { nodes };

  const handleSearch = (event: any) => {
    setSearch(event.target.value);
  };

  // Фильтрация через поиск
  data = {
    nodes: data.nodes?.filter((item) => (search ? item.name?.toLowerCase().includes(search.toLowerCase()) : true)),
  };

  const columns: ColumnDef<TriggersTableData>[] = [
    {
      header: " ",
      size: 60,
      maxSize: 60,
      minSize: 60,
      cell: ({ row }) => (row.original.enabled ? <TbCircleFilled className="text-green-600" /> : <TbCircleFilled className="text-destructive"/>),
    },
    {
      header: t("trigger_name"),
      size: 1000,
      accessorFn: (row) => `${row.name}`,
      cell: ({ row }) => row.original.name,
    },
    {
      header: t("trigger_value"),
      size: 300,
      cell: ({ row }) => {
        switch (row.original.type) {
          case "Sites::Triggers::RfidIn":
          case "Sites::Triggers::RfidOut":
          case "Sites::Triggers::RfidSwitch":
            return `${t("card")}: ${row.original.string_value_equal}`;
          case "Sites::Triggers::ValueLessThan":
            return `${t('less_than')} ${row.original.value_less_than}`;
          case "Sites::Triggers::ValueMoreThan":
            return `${t('more_than')} ${row.original.value_more_than}`;
        }
      },
    },
  ];

  function selectTrigger(item: any) {
    setSelectedTrigger(item.id);
    setOpenEditModal(true);
  }

  function addTrigger() {
    setOpenCreateModal(true);
  }

  return (
    <>
      <div className="flex flex-col w-full">
        <div className="flex flex-row w-full h-14 items-center gap-4 px-6 justify-between">
          <Input
            className="h-8"
            placeholder={t("search")}
            onChange={handleSearch}
            value={search}
            autoComplete="off"
            width="100%"
          />

          <RoleRequire role="maintainer">
            <Button variant={"default"} size={"md"} onClick={() => addTrigger()}>
              {t("create_trigger")}
            </Button>
          </RoleRequire>
        </div>
      </div>

      {!data.nodes?.length ? ( // Заглушка для отсутствия данных при поиске
        <div className="flex w-full h-full justify-center items-center">{t("no_data")}</div>
      ) : (
        <SimpleDataTable data={data.nodes} columns={columns} onClickRow={selectTrigger} />
      )}
      {openCreateModal && <AddTriggerDialog open={openCreateModal} onChange={setOpenCreateModal} />}
      {selectedTrigger && openEditModal && (
        <EditTriggerDialog id={selectedTrigger!} open={openEditModal} onChange={setOpenEditModal} />
      )}
    </>
  );
}

function AddTriggerDialog({ open, onChange }: { open: boolean; onChange: (state: boolean) => void }) {
  const closeEditModal = () => {
    onChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={() => onChange(!open)}>
      <DialogOverlay className="bg-slate-950/40 data-[state=open]:animate-overlayShow fixed inset-0" />
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("adding_trigger")}</DialogTitle>
          <DialogDescription>{t("adding_trigger_desc")}</DialogDescription>
          <AddTrigger closeEditModal={closeEditModal} />
        </DialogHeader>
      </DialogContent>
    </Dialog>
  );
}

function EditTriggerDialog({ id, open, onChange }: { id: string; open: boolean; onChange: (state: boolean) => void }) {
  const closeEditModal = () => {
    onChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={() => onChange(!open)}>
      <DialogOverlay className="bg-slate-950/40 data-[state=open]:animate-overlayShow fixed inset-0" />
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("editing_trigger")}</DialogTitle>
          <DialogDescription>{t("editing_trigger_desc")}</DialogDescription>
          <TriggerItem id={id} closeEditModal={closeEditModal} />
        </DialogHeader>
      </DialogContent>
    </Dialog>
  );
}
